import { useAssistantNavigation } from "@/hooks/useAssistantNavigation";
import { isNativeApp } from "@/lib/native";
import { groupNavigationItems } from "@/utils/navigationGroups";
import { allowNativeNavigation } from "@/hooks/useNativeDraft";
import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Bot, Calendar, Clock, Bell, UserCheck, Menu, X, LogOut, Wrench, Shirt,
  User, FolderOpen, Mail, Code, Plane, BookOpen, GraduationCap, CalendarCheck, LayoutDashboard, CheckSquare
} from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/apiClient";
import { usePortal } from "@/lib/PortalContext";
import { useAuth } from "@/lib/AuthContext";
import { createPageUrl } from "@/utils";
import { useFeaturedEvent } from "@/hooks/useFeaturedEvent";
import { recordId as getId } from "@/utils/currentEvent";
import { displayName } from "@/utils/displayName";

export function FeaturedEventCard() {
  const { user } = useAuth();
  const { event, type } = useFeaturedEvent(user);
  if (!event) return null;
  return <Link className="mx-3 mt-2 flex items-center gap-3 rounded-lg border border-yellow-300 bg-yellow-50 p-3 text-black" to={`/event/${getId(event)}`}>
    <CalendarCheck className="h-6 w-6 shrink-0" />
    <div className="min-w-0"><p className="text-xs font-medium">{type === 'current' ? 'Current event' : 'Next confirmed event'}</p><p className="font-semibold break-words">{event.name}</p><p className="text-sm">{event.start_date?.slice(0, 10)}{event.end_date && ` – ${event.end_date.slice(0, 10)}`}</p></div>
  </Link>;
}

function MoreMenu({ open, onClose }) {
  const assistantNavigation = useAssistantNavigation();
  const location = useLocation();
  const navigate = useNavigate();
  const { isManagerView, isActualAdmin, portalMode, setPortalMode } = usePortal();
  const { user, logout } = useAuth();
  const handleClose = onClose;

  const handleNav = async (url, external) => {
    if (!allowNativeNavigation()) return;
    if (external) {
      if (isNativeApp()) {
        const { Browser } = await import("@capacitor/browser");
        await Browser.open({ url });
      } else window.open(url, "_blank", "noopener,noreferrer");
    } else {
      navigate(url);
    }
    handleClose();
  };

  const handleLogout = () => {
    handleClose();
    if (allowNativeNavigation()) logout();
  };

  const managerItems = [
    { title: "Flights", url: "/flights", icon: Plane },
    { title: "Staff", url: createPageUrl("staff"), icon: UserCheck },
    { title: "Documents", url: createPageUrl("document-management"), icon: FolderOpen },
    { title: "Clock In/Out", url: createPageUrl("clock"), icon: Clock },
    { title: "Notification Settings", url: createPageUrl("notification-settings"), icon: Bell },
    // SKILLS_MATRIX_REMOVED: the old Skills Matrix page was removed; planning skills now live in Admin.
    { title: "Notification Preferences", url: createPageUrl("notification-preferences"), icon: Mail },
    { title: "Developer", url: createPageUrl("admin"), icon: Code },
    { title: "Faulty Equipment", url: "/faulty-equipment", icon: Wrench },
    { title: "Documentation", url: "/documentation", icon: BookOpen },
    { title: "Assistant", url: "/assistant", icon: Bot },
  ];

  const freelancerItems = [
    { title: "My Profile", url: createPageUrl("profile"), icon: User },
    ...(user?.can_view_all_flights ? [{ title: "Flights", url: "/flights", icon: Plane }] : []),
    { title: "My Availability", url: "/availability", icon: Calendar },
    { title: "Notification Preferences", url: "/notification-preferences", icon: Bell },
    { title: "Uniform Request", url: "/uniform-requests", icon: Shirt },
    { title: "My Documents", url: createPageUrl("documents"), icon: FolderOpen },
    { title: "iHASCO Training", url: createPageUrl("training"), icon: GraduationCap },
    { title: "Faulty Equipment", url: "/faulty-equipment", icon: Wrench },
    { title: "Documentation", url: "/documentation", icon: BookOpen },
    { title: "Assistant", url: "/assistant", icon: Bot },
  ];

  const menuItems = user?.profile_complete === false && !isManagerView ? [] : isManagerView ? managerItems : freelancerItems;
  const menuGroups = groupNavigationItems(menuItems.filter(item => item.url !== "/assistant" || assistantNavigation.visible));

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(value) => { if (!value) onClose(); }}>
      <DialogContent className="native-more p-0 gap-0 [&>button]:hidden" aria-describedby={undefined}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <DialogTitle>More</DialogTitle>
          <button aria-label="Close menu" onClick={handleClose} className="p-2 -mr-2 rounded-full hover:bg-gray-100">
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <div className="flex-1 overflow-auto px-5 py-4">
          <button
            onClick={() => handleNav(createPageUrl("profile"))}
            className="flex items-center gap-3 w-full p-3 rounded-xl hover:bg-gray-50 mb-2"
          >
            {user?.profile_photo_url ? (
              <img
                src={user.profile_photo_url}
                alt={`${displayName(user) || 'Your'} profile photo`}
                className="w-10 h-10 rounded-full object-cover"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = 'none';
                  e.target.parentElement.querySelector('[data-fallback]').style.display = '';
                }}
              />
            ) : null}
            <div
              data-fallback
              className="w-10 h-10 bg-gradient-to-br from-[rgb(var(--brand-yellow))] to-[rgb(var(--brand-yellow-hover))] rounded-full flex items-center justify-center"
              style={user?.profile_photo_url ? { display: 'none' } : undefined}
            >
              <span className="text-black font-semibold text-sm">
                {displayName(user).charAt(0) || "U"}
              </span>
            </div>
            <div className="text-left">
              <p className="font-medium text-gray-900 text-sm">
                {displayName(user) || "User"}
              </p>
              <p className="text-xs text-gray-500">{user?.email}</p>
            </div>
          </button>

          {isActualAdmin && (
            <div className="flex gap-2 mb-4 px-1">
              <button
                onClick={() => setPortalMode("manager")}
                className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${
                  portalMode === "manager"
                    ? "bg-[rgb(var(--brand-yellow))] text-black"
                    : "bg-gray-100 text-gray-600"
                }`}
              >
                Manager
              </button>
              <button
                onClick={() => setPortalMode("staff")}
                className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${
                  portalMode === "staff"
                    ? "bg-[rgb(var(--brand-yellow))] text-black"
                    : "bg-gray-100 text-gray-600"
                }`}
              >
                Event Portal
              </button>
            </div>
          )}

          <nav aria-label="More navigation" className="space-y-4">
            {menuGroups.map((group) => (
            <section key={group.title} aria-label={group.title} className="border-t border-gray-200 pt-3">
              <h2 className="mb-1 rounded-md bg-gray-100 px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-gray-950">{group.title}</h2>
            {group.items.map((item) => {
              const Icon = item.icon;
              const active = !item.external && (
                location.pathname === item.url || location.pathname.startsWith(item.url + "/")
              );
              return (
                <button
                  key={item.title}
                  onClick={() => handleNav(item.url, item.external)}
                  className={`flex items-center gap-3 w-full px-3 py-3 rounded-xl transition-colors ${
                    active ? "bg-[rgb(var(--brand-yellow))]/10 text-black" : "text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <Icon className={`w-5 h-5 ${active ? "text-black" : "text-gray-600"}`} />
                  <span className="font-medium text-sm">{item.title}</span>
                </button>
              );
            })}
            </section>
            ))}
          </nav>
        </div>

        <div className="px-5 pb-4 border-t border-gray-200 pt-4">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-3 py-3 rounded-xl text-red-600 hover:bg-red-50 transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-medium text-sm">Sign Out</span>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function BottomTabBar() {
  const location = useLocation();
  const { user } = useAuth();
  const [moreOpen, setMoreOpen] = useState(false);
  const { isManagerView } = usePortal();
  const { event, type } = useFeaturedEvent(user);
  const navigate = useNavigate();
  const { data: notifications = [] } = useQuery({ queryKey: ["notifications", user?.email], queryFn: () => api.entities.Notification.list(), enabled: !!getId(user), refetchInterval: 60000 });
  const leftTabs = [
    { label: "Dashboard", icon: LayoutDashboard, path: "/dashboard" },
    { label: "Events", icon: Calendar, path: "/events" },
    { label: "Clock", icon: Clock, path: "/clock" },
  ];

  const rightTabs = [
    { label: "Tasks", icon: CheckSquare, path: "/tasks" },
    { label: "Alerts", icon: Bell, path: "/notifications", badge: notifications.filter(n => !n.is_read).length },
  ];

  const isActive = (path) => {
    if (path === "/dashboard")
      return location.pathname === "/" || location.pathname.startsWith("/dashboard");
    if (path === "/events")
      return location.pathname.startsWith("/events") || location.pathname.startsWith("/event/");
    return location.pathname.startsWith(path);
  };

  const renderTab = (tab) => {
    const active = isActive(tab.path);
    const Icon = tab.icon;
    return (
      <Link
        key={tab.path}
        aria-current={active ? "page" : undefined}
        to={tab.path}
        className="min-w-0 flex-1 flex flex-col items-center justify-center relative"
      >
        {active && (
          <div className="absolute top-0 left-4 right-4 h-0.5 bg-[rgb(var(--brand-yellow))] rounded-b" />
        )}
        <div className="relative">
          <Icon className={`w-6 h-6 ${active ? "text-black" : "text-gray-600"}`} />
          {tab.badge > 0 && (
            <Badge className="absolute -top-1.5 -right-2.5 h-4 min-w-4 flex items-center justify-center p-0 px-1 bg-red-600 text-white text-[10px] leading-none">
              {tab.badge > 9 ? "9+" : tab.badge}
            </Badge>
          )}
        </div>
        <span className={`mt-0.5 max-w-full truncate px-0.5 text-[9px] sm:text-[10px] font-medium ${active ? "text-black" : "text-gray-600"}`}>
          {tab.label}
        </span>
      </Link>
    );
  };

  return (
    <>
      <nav aria-label="Main navigation" className="native-bottom-nav fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200">
        <div className="flex items-stretch" style={{ height: "56px" }}>
          {(user?.profile_complete === false && !isManagerView ? [{ label: "Profile", path: "/profile", icon: User }] : leftTabs).map(renderTab)}

          {(user?.profile_complete !== false || isManagerView) && <button
            disabled={!event}
            onClick={() => { if (event && allowNativeNavigation()) navigate(`/event/${getId(event)}`); }}
            aria-label={event ? `${type === 'next' ? 'Next event' : 'Current event'}: ${event.name}` : 'No confirmed event'}
            className="relative min-w-0 flex-1 flex flex-col items-center justify-center -mt-3">
            <span className={`w-14 h-14 rounded-full flex items-center justify-center shadow-lg border-4 border-white ${event ? 'bg-[rgb(var(--brand-yellow))] text-black' : 'bg-gray-200 text-gray-400'}`}><CalendarCheck className="w-6 h-6" /></span>
            <span className="text-[9px] font-medium text-gray-700">{type === 'next' ? 'Next' : 'Event'}</span>
          </button>}

          {(user?.profile_complete === false && !isManagerView ? [] : rightTabs).map(renderTab)}

          <button
            onClick={() => setMoreOpen(true)}
            className="min-w-0 flex-1 flex flex-col items-center justify-center relative"
          >
            {moreOpen && (
              <div className="absolute top-0 left-4 right-4 h-0.5 bg-[rgb(var(--brand-yellow))] rounded-b" />
            )}
            <Menu className={`w-6 h-6 ${moreOpen ? "text-black" : "text-gray-600"}`} />
            <span className={`mt-0.5 max-w-full truncate px-0.5 text-[9px] sm:text-[10px] font-medium ${moreOpen ? "text-black" : "text-gray-600"}`}>
              More
            </span>
          </button>
        </div>
        <div style={{ paddingBottom: "env(safe-area-inset-bottom)" }} />
      </nav>

      <MoreMenu open={moreOpen} onClose={() => setMoreOpen(false)} />
    </>
  );
}
